import { Plus } from 'lucide-react';
import { parseAsString, useQueryStates } from 'nuqs';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ORG_STATUSES, ORG_STATUS_LABEL, SUBSCRIPTION_STATUSES, SUBSCRIPTION_STATUS_LABEL, type PlatformOrgRow, type PlatformPage } from '@barbeiropro/contracts';
import { Button } from '@/components/ui/button';
import { DataTable, type DataColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { useTableParams } from '@/components/shared/use-table-params';
import { CreateOrgModal } from './create-org-modal';
import { usePlans } from './plans-query';
import { DownloadButton, dd, FilterSelect, OrgPill, SubPill, useList, usePlatformAbility } from './shared';

export { usePlans } from './plans-query';

export function OrgsPage() {
  const params = useTableParams({ sort: 'createdAt', order: 'desc' });
  const [f, setF] = useQueryStates({ status: parseAsString, subscriptionStatus: parseAsString, planId: parseAsString }, { history: 'replace' });
  const navigate = useNavigate();
  const plans = usePlans();
  const { isOwner, canModerate } = usePlatformAbility();
  const [creating, setCreating] = useState(false);
  const query = { ...params.query, status: f.status ?? undefined, subscriptionStatus: f.subscriptionStatus ?? undefined, planId: f.planId ?? undefined };
  const list = useList<PlatformPage<PlatformOrgRow>>('orgs', '/platform/orgs', query);

  const columns: DataColumn<PlatformOrgRow>[] = [
    { id: 'name', header: 'Barbearia', meta: { sortKey: 'name' }, cell: ({ row }) => (<span><span className="font-medium">{row.original.name}</span><span className="block text-xs text-muted-foreground">{row.original.email ?? row.original.slug}</span></span>) },
    { id: 'status', header: 'Situação', meta: { sortKey: 'status' }, cell: ({ row }) => <OrgPill status={row.original.status} /> },
    { id: 'planName', header: 'Plano', meta: { sortKey: 'planName' }, cell: ({ row }) => row.original.planName ?? '—' },
    { id: 'sub', header: 'Assinatura', cell: ({ row }) => <SubPill status={row.original.subscriptionStatus} /> },
    { id: 'usersCount', header: 'Usuários', meta: { sortKey: 'usersCount', align: 'right' }, cell: ({ row }) => row.original.usersCount },
    { id: 'createdAt', header: 'Cadastro', meta: { sortKey: 'createdAt' }, cell: ({ row }) => <span className="tabular whitespace-nowrap">{dd(row.original.createdAt)}</span> },
  ];

  return (
    <div>
      <PageHeader
        title="Organizações"
        description="Barbearias clientes da plataforma."
        actions={
          <>
            <DownloadButton path="/platform/orgs/export" query={query} filename="organizacoes.xlsx" />
            {(isOwner || canModerate) && (
              <Button onClick={() => setCreating(true)}>
                <Plus className="size-4" /> Nova organização
              </Button>
            )}
          </>
        }
      />
      <DataTable
        columns={columns}
        data={list.data}
        params={params}
        isLoading={list.isLoading}
        error={list.error}
        onRetry={list.refetch}
        searchPlaceholder="Buscar por nome, e-mail ou endereço"
        onRowClick={(r) => navigate(`/plataforma/organizacoes/${r.id}`)}
        empty={{ title: 'Nenhuma organização encontrada' }}
        filters={
          <>
            <FilterSelect label="Situação" value={f.status} onChange={(v) => { setF({ status: v }); params.set({ page: 1 }); }} options={ORG_STATUSES.map((s) => ({ value: s, label: ORG_STATUS_LABEL[s] }))} />
            <FilterSelect label="Assinatura" value={f.subscriptionStatus} onChange={(v) => { setF({ subscriptionStatus: v }); params.set({ page: 1 }); }} options={SUBSCRIPTION_STATUSES.map((s) => ({ value: s, label: SUBSCRIPTION_STATUS_LABEL[s] }))} />
            <FilterSelect label="Plano" value={f.planId} onChange={(v) => { setF({ planId: v }); params.set({ page: 1 }); }} options={(plans.data ?? []).map((p) => ({ value: p.id, label: p.name }))} />
          </>
        }
      />
      <CreateOrgModal open={creating} onOpenChange={setCreating} />
    </div>
  );
}
